import type { FC } from 'react';
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { SponsorGallery } from '@/components/home/SponsorGallery';
import { CountdownTimer, FadeIn } from '@/components/shared';
import { useCountdown, useAuth } from '@/hooks';
import { supabase } from '@/lib/api/supabase';
import { REGISTRATIONS_CLOSED, CLOSED_HEADLINE } from '@/lib/registrationsClosed';

/**
 * Home page component
 * Main landing page for the event management application
 */
export const HomePage: FC = () => {
  const { registrationBlocked, targetDate, isLoading: countdownLoading } = useCountdown();
  const { user } = useAuth();
  const [heroImageUrl, setHeroImageUrl] = useState<string>('/1080x566_banner.webp');

  // Load hero image URL from database
  useEffect(() => {
    const loadHeroImage = async () => {
      const { data } = await supabase
        .from('site_settings')
        .select('setting_value')
        .eq('setting_key', 'hero_image_url')
        .single();

      if (data?.setting_value) {
        setHeroImageUrl(data.setting_value);
      }
    };

    loadHeroImage();
  }, []);

  return (
    <div className="bg-gradient-to-b from-turquoise-800 via-turquoise-700 to-turquoise-800 min-h-screen">
      {/* Hero Section with Background */}
      <section className="relative min-h-[600px] flex items-center justify-center overflow-hidden">
        {/* Background Image */}
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url('${heroImageUrl}')` }}
        >
          {/* Dark overlay for better text readability */}
          <div className="absolute inset-0 bg-black/40"></div>
        </div>

        {/* Hero Content Card */}
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-xl mx-auto">
            <FadeIn delay={0.1} duration={0.6}>
              <Card className="backdrop-blur-md bg-turquoise-800/90 shadow-2xl border-2 border-orange-500">
              <CardHeader className="text-center pb-3">
                <h1 className="text-5xl font-viking text-white mb-3 drop-shadow-lg">
                  Cactus Cup
                </h1>
                <p className="text-2xl font-medium text-white mb-2">
                  Hottest HEMA Event of the Summer
                </p>
                <div className="w-20 h-1 bg-gradient-to-r from-orange-500 to-orange-600 mx-auto mt-3 rounded-full"></div>
              </CardHeader>

              <CardContent className="pb-4">
                {/* Event Details Grid */}
                <div className="grid grid-cols-1 gap-3 mb-6">
                  <div className="flex items-center gap-3 justify-center">
                    <div className="p-2 bg-orange-500 rounded-lg">
                      <Calendar className="h-5 w-5 text-white" />
                    </div>
                    <div className="text-left">
                      <p className="text-xs text-orange-300 font-medium">Date</p>
                      <p className="font-semibold text-white">July 17-19, 2026</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 justify-center">
                    <div className="p-2 bg-orange-500 rounded-lg">
                      <MapPin className="h-5 w-5 text-white" />
                    </div>
                    <div className="text-left">
                      <p className="text-xs text-orange-300 font-medium">Location</p>
                      <p className="font-semibold text-white">Mesa, Arizona</p>
                    </div>
                  </div>
                </div>

                {/* CTA Buttons / Countdown Timer */}
                <div className="flex flex-col gap-3 justify-center">
                  {REGISTRATIONS_CLOSED ? (
                    <div className="text-center space-y-4">
                      <h2 className="text-2xl font-semibold text-orange-300">
                        {CLOSED_HEADLINE}
                      </h2>
                      {user && (
                        <Link to="/dashboard">
                          <Button size="lg" className="w-full text-base px-8 bg-orange-500 hover:bg-orange-600 text-white">
                            Dashboard
                          </Button>
                        </Link>
                      )}
                      <Link to="/events">
                        <Button size="lg" variant="outline" className="w-full text-base px-8 bg-white/10 border-white text-white hover:bg-white/20">
                          View Tournaments
                        </Button>
                      </Link>
                    </div>
                  ) : registrationBlocked && targetDate ? (
                    <div className="text-center space-y-4">
                      <h3 className="text-xl font-semibold text-orange-300">
                        Registration Opens In:
                      </h3>
                      <CountdownTimer targetDate={targetDate} className="scale-75" />
                      <p className="text-sm text-white/80">
                        Registration will be available on {new Date(targetDate).toLocaleDateString()} at {new Date(targetDate).toLocaleTimeString()}
                      </p>
                    </div>
                  ) : (
                    <>
                      {user ? (
                        <Link to="/dashboard">
                          <Button size="lg" className="w-full text-base px-8 bg-orange-500 hover:bg-orange-600 text-white">
                            Dashboard
                          </Button>
                        </Link>
                      ) : (
                        <Link to="/register">
                          <Button size="lg" variant="secondary" className="w-full text-base px-8" disabled={countdownLoading}>
                            {countdownLoading ? 'Loading...' : 'Register Now'}
                          </Button>
                        </Link>
                      )}
                      <Link to="/events">
                        <Button size="lg" variant="outline" className="w-full text-base px-8 bg-white/10 border-white text-white hover:bg-white/20">
                          View Tournaments
                        </Button>
                      </Link>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
            </FadeIn>
          </div>
        </div>
      </section>

      <div className="container mx-auto px-4 py-16">

      {/* Featured Content */}
      <section className="grid md:grid-cols-3 gap-6 mt-16">
        <FadeIn delay={0.2}>
          <Link to="/events" className="block group">
            <Card className="transition-all duration-300 group-hover:scale-[1.02] group-hover:shadow-[0_0_20px_rgba(20,184,166,0.3)] bg-gradient-to-br from-turquoise-600 to-turquoise-700 group-hover:from-turquoise-500 group-hover:to-turquoise-600 border-2 border-turquoise-500 group-hover:border-orange-400">
              <CardHeader>
                <CardTitle>Tournaments</CardTitle>
                <CardDescription className="text-orange-200">
                  Compete in longsword, saber, and more
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-orange-100">
                  Multiple tournaments for all skill levels. From beginner to advanced competitors.
                </p>
              </CardContent>
            </Card>
          </Link>
        </FadeIn>

        <FadeIn delay={0.3}>
          <Link to="/instructors" className="block group">
            <Card className="transition-all duration-300 group-hover:scale-[1.02] group-hover:shadow-[0_0_20px_rgba(20,184,166,0.3)] bg-gradient-to-br from-turquoise-600 to-turquoise-700 group-hover:from-turquoise-500 group-hover:to-turquoise-600 border-2 border-turquoise-500 group-hover:border-orange-400">
              <CardHeader>
                <CardTitle>Instructors</CardTitle>
                <CardDescription className="text-orange-200">
                  Learn from expert instructors
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-orange-100">
                  Hands-on workshops covering techniques, tactics, and historical context.
                </p>
              </CardContent>
            </Card>
          </Link>
        </FadeIn>

        <FadeIn delay={0.4}>
          <Link to="/special-events" className="block group">
            <Card className="transition-all duration-300 group-hover:scale-[1.02] group-hover:shadow-[0_0_20px_rgba(20,184,166,0.3)] bg-gradient-to-br from-turquoise-600 to-turquoise-700 group-hover:from-turquoise-500 group-hover:to-turquoise-600 border-2 border-turquoise-500 group-hover:border-orange-400">
              <CardHeader>
                <CardTitle>Special Events</CardTitle>
                <CardDescription className="text-orange-200">
                  Join us for unique experiences
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-orange-100">
                  Evening socials, formal dinners, and special competitions throughout the weekend.
                </p>
              </CardContent>
            </Card>
          </Link>
        </FadeIn>
      </section>

      </div>

      {/* Sponsor Gallery */}
      <SponsorGallery />
    </div>
  );
};
